import { pmRequest } from "./client";

// Chronos lit PM et n'écrit que la fiche projet (création, modification),
// proposée aux seuls admins. PM exige le scope, et Cerbos en modification.

export interface PmProject {
  id: string;
  tenant_id: string;
  code: string;
  name: string;
  description?: string;
  status: "active" | "on_hold" | "completed" | "cancelled" | string;
  type: string;
  priority: string;
  owner_id: string;
  start_date?: string;
  end_date?: string;
  progress_pct: number;
  /** Budget estimé en unités entières de `budget_currency`. */
  budget_estimated?: number;
  budget_currency?: string;
  /** Référence MDM du client du projet, lue telle quelle. */
  client_party_url?: string;
}

export interface PmWorkItem {
  id: string;
  tenant_id: string;
  project_id: string;
  parent_id?: string;
  path: string;
  type: string;
  title: string;
  description?: string;
  status: string;
  priority: string;
  assignee_ids: string[];
  reporter_id: string;
  start_date?: string;
  due_date?: string;
  completed_at?: string;
  progress_pct: number;
  tags: string[];
  position: number;
}

export interface PmMilestone {
  id: string;
  tenant_id: string;
  project_id: string;
  name: string;
  description?: string;
  target_date: string;
  status: string;
  validator_ids: string[];
  validated_by?: string;
  validated_at?: string;
}

export interface PmDashboard {
  project: {
    id: string;
    code: string;
    name: string;
    status: string;
    priority: string;
    progress_pct: number;
    owner_id: string;
    start_date?: string;
    end_date?: string;
  };
  work_items: {
    total: number;
    by_status: Record<string, number>;
    done: number;
    overdue: number;
    completion_pct: number;
  };
  milestones: {
    total: number;
    by_status: Record<string, number>;
    validated: number;
  };
  generated_at: string;
}

export interface PmList<T> {
  items: T[];
  next_cursor?: string;
}

// ponytail: pages bornées au max backend (200) au lieu du défaut 50, qui
// tronquait silencieusement listes et compteurs. Les hooks drainent next_cursor
// via fetchAllPages ; passer à useInfiniteQuery si les volumes explosent.
export function listProjects(cursor?: string): Promise<PmList<PmProject>> {
  return pmRequest({
    method: "GET",
    url: "/projects",
    params: { limit: 200, ...(cursor ? { cursor } : {}) },
  });
}

export function getProject(id: string): Promise<PmProject> {
  return pmRequest({ method: "GET", url: `/projects/${id}` });
}

/** Champs éditables d'une fiche projet. Les dates sont au format AAAA-MM-JJ. */
export interface PmProjectFields {
  name: string;
  description?: string;
  start_date?: string;
  end_date?: string;
  budget_estimated?: number;
  budget_currency?: string;
}

export interface PmProjectInput extends PmProjectFields {
  code: string;
}

export function createProject(input: PmProjectInput): Promise<PmProject> {
  return pmRequest({ method: "POST", url: "/projects", data: input });
}

export function updateProject(
  id: string,
  patch: Partial<PmProjectFields>,
): Promise<PmProject> {
  return pmRequest({ method: "PATCH", url: `/projects/${id}`, data: patch });
}

export function listWorkItems(
  projectId: string,
  cursor?: string,
): Promise<PmList<PmWorkItem>> {
  return pmRequest({
    method: "GET",
    url: `/projects/${projectId}/work-items`,
    params: { limit: 200, ...(cursor ? { cursor } : {}) },
  });
}

export function listMilestones(
  projectId: string,
  cursor?: string,
): Promise<PmList<PmMilestone>> {
  return pmRequest({
    method: "GET",
    url: `/projects/${projectId}/milestones`,
    params: { limit: 200, ...(cursor ? { cursor } : {}) },
  });
}

export function getProjectDashboard(projectId: string): Promise<PmDashboard> {
  return pmRequest({ method: "GET", url: `/projects/${projectId}/dashboard` });
}
